import {
  DeferredCodeBlock as CodeBlock,
  frameworkCreateProjectCodeSnippet,
  frameworkLabels,
  invalidateDocumentListData,
} from "@prismicio/editor-fields"
import { localStorage } from "@prismicio/editor-support/DOM"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardImage,
  CardVideo,
  Separator,
  Skeleton,
  Text,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { z } from "zod"

import {
  onboardingSelectedProject,
  onboardingStep,
  onboardingWithImages,
  onboardingWithVideos,
} from "../app/constants"
import { useOnboardingDropOffFlag } from "../app/integrations/AmplitudeExperiments"
import { getBaseRepositoryUrl, getRepositoryName } from "../app/routing"
import {
  fetchCustomTypes,
  invalidateCustomTypes,
} from "../app/services/customTypes"
import { useRepository } from "../app/services/repository"
import { useSharedOnboarding } from "../app/services/sharedOnboarding"
import { trackPbOnboardingStep } from "../app/services/tracking"
import { useHasCustomTypes } from "../documents/DocumentListBody/useHasCustomTypes"
import { useSharedOnboardingParams } from "../documents/DocumentListSidePanel/useSharedOnboardingParams"
import { getMasterLocale } from "../settings/locales/localeUtils"
import { usePrefetchDefaultLocalesQuery } from "../settings/locales/useDefaultLocales"
import {
  useAddRepositoryLocaleMutation,
  usePrefetchRepositoryLocalesQuery,
  useRepositoryLocalesQuery,
} from "../settings/locales/useRepositoryLocales"
import { ChooseMainLocaleStep } from "./ChooseMainLocaleStep"
import { OnboardingFeedback } from "./OnboardingFeedback"
import { UpfrontStep } from "./UpfrontStep"

export function OnboardingSteps() {
  const { currentStep, sharedOnboardingStepParam } = useOnboarding()

  const Component = stepMap[currentStep]

  if (!Component) return null

  return (
    <Box position="relative" width="100%" justifyContent="center">
      <Component shouldAnimate={!!sharedOnboardingStepParam} />
      <OnboardingFeedback currentStep={currentStep} />
    </Box>
  )
}

const stepMap = {
  welcome: WelcomeStep,
  chooseMainLanguage: ChooseMainLocaleStep,
  launchStarter: LaunchStarterStep,
  pushModels: PushModelsStep,
  createProject: CreateProjectStep,
  setupSliceMachine: SetupSliceMachineStep,
  runSliceMachine: RunSliceMachineStep,
  upfront: UpfrontStep,
} as const

const starterSteps = ["welcome", "launchStarter"] as const
const starterStepsSchema = z.enum(starterSteps).catch("welcome")

const nonStarterSteps = [
  "welcome",
  "chooseMainLanguage",
  "createProject",
  "setupSliceMachine",
  "runSliceMachine",
  "pushModels",
] as const
const nonStarterStepsSchema = z.enum(nonStarterSteps).catch("welcome")

const upfrontSteps = ["chooseMainLanguage", "upfront"] as const
const upfrontStepsSchema = z.enum(upfrontSteps).catch("upfront")

export type OnboardingStep =
  | z.infer<typeof starterStepsSchema>
  | z.infer<typeof nonStarterStepsSchema>
  | z.infer<typeof upfrontStepsSchema>
function useSharedOnboardingValidParams() {
  const { onboardingStep, deleteStepParam } = useSharedOnboardingParams()
  const { data: repositoryLocales } = useRepositoryLocalesQuery()
  const hasChosenLocale = Boolean(getMasterLocale(repositoryLocales))

  const isTryingToSkipLocaleStep = useCallback(() => {
    return onboardingStep !== "chooseMainLanguage" && !hasChosenLocale
  }, [onboardingStep, hasChosenLocale])

  function isValidStep(step: string): step is OnboardingStep {
    return Boolean(step && step in stepMap)
  }

  function getValidOnBoardingStep() {
    if (!onboardingStep) return undefined
    if (isTryingToSkipLocaleStep()) return "chooseMainLanguage"
    if (isValidStep(onboardingStep)) return onboardingStep
    return undefined
  }

  useEffect(() => {
    // Sync the validation done at render time with the query params
    // This has to be handled in a useEffect to avoid the following console warning:
    // "You should call navigate() in a React.useEffect(), not when your component is first rendered. Error Component Stack"
    if (isTryingToSkipLocaleStep()) deleteStepParam()
  }, [isTryingToSkipLocaleStep, deleteStepParam])

  return { onboardingStep: getValidOnBoardingStep(), deleteStepParam }
}

export function useOnboarding() {
  const { starterId } = useRepository()

  const sharedOnboardingParams = useSharedOnboardingValidParams()
  const onboardingDropOffFlag = useOnboardingDropOffFlag()
  const { data: repositoryLocales } = useRepositoryLocalesQuery()
  const hasMasterLocale = Boolean(getMasterLocale(repositoryLocales))
  const addRepositoryLocaleMutation = useAddRepositoryLocaleMutation()
  const { framework } = useRepository()
  const isSupportedFramework = getIsSupportedFramework(framework)
  const { steps, stepSchema } = getOnboardingConfig({
    hasStarterId: Boolean(starterId),
    onboardingDropOffFlag,
    isSupportedFramework,
  })

  const currentStep = useSyncExternalStore(localStorage.subscribe, () =>
    stepSchema.parse(localStorage.read(onboardingStep)),
  )

  /**
   * This is a temporary onboarding experiment to skip the locale creation step.
   * We will remove this once the experiment is over.
   */
  const { mutate: addDefaultLocale } = useMutation({
    mutationFn: async () => {
      return await addRepositoryLocaleMutation.mutateAsync({
        id: "en-us",
        isMaster: true,
        customName: null,
      })
    },
    onError: (error) => {
      // If the locale setup fails, we don't want to block the onboarding.
      console.error(
        "Error setting up the locale for the onboarding drop off experiment",
        error,
      )

      // To ensure the user doesn't get stuck in the onboarding,
      // we set the current step to the locale step.
      localStorage.write(onboardingStep, "chooseMainLanguage")
    },
  })

  const shouldAddLocale =
    onboardingDropOffFlag === "upfront" &&
    !hasMasterLocale &&
    isSupportedFramework

  useEffect(() => {
    if (shouldAddLocale) {
      addDefaultLocale()
    }
  }, [shouldAddLocale, addDefaultLocale])

  function goToNextStep() {
    const nextStepIndex = steps.findIndex((step) => step === currentStep) + 1
    const nextStep = steps[nextStepIndex]
    if (nextStep) {
      localStorage.write(onboardingStep, nextStep)
      sharedOnboardingParams.deleteStepParam()
    }
  }

  function goToPreviousStep() {
    const previousStepIndex =
      steps.findIndex((step) => step === currentStep) - 1
    const previousStep = steps[previousStepIndex]
    if (previousStep) {
      localStorage.write(onboardingStep, previousStep)
      sharedOnboardingParams.deleteStepParam()
    }
  }

  useEffect(() => {
    if (sharedOnboardingParams.onboardingStep) {
      localStorage.write(onboardingStep, sharedOnboardingParams.onboardingStep)
    }
  }, [sharedOnboardingParams.onboardingStep])

  return {
    currentStep,
    steps,
    goToNextStep,
    goToPreviousStep,
    sharedOnboardingStepParam: sharedOnboardingParams.onboardingStep,
  }
}

interface getOnboardingConfigArgs {
  hasStarterId: boolean
  onboardingDropOffFlag: ReturnType<typeof useOnboardingDropOffFlag>
  isSupportedFramework: boolean
}

function getOnboardingConfig(args: getOnboardingConfigArgs) {
  const { hasStarterId, onboardingDropOffFlag, isSupportedFramework } = args

  if (
    onboardingDropOffFlag === "upfront" &&
    isSupportedFramework &&
    !hasStarterId
  ) {
    return {
      steps: upfrontSteps,
      stepSchema: upfrontStepsSchema,
    }
  }

  return hasStarterId
    ? { steps: starterSteps, stepSchema: starterStepsSchema }
    : { steps: nonStarterSteps, stepSchema: nonStarterStepsSchema }
}

export function getIsSupportedFramework(
  framework: string,
): framework is "next" | "nuxt" | "sveltekit" {
  return (
    framework === "next" || framework === "nuxt" || framework === "sveltekit"
  )
}

export function useRevalidateCustomTypes() {
  const [isRevalidating, setIsRevalidating] = useState(false)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showToast } = useToast()

  async function revalidate() {
    setIsRevalidating(true)

    try {
      void invalidateCustomTypes()
      const customTypes = await fetchCustomTypes()

      if (customTypes.length === 0) {
        showToast({
          title: "It looks like you haven't pushed your models yet",
          icon: "alert",
        })
      } else {
        await invalidateDocumentListData(queryClient)

        showToast({
          title: "You pushed your models successfully",
        })

        navigate("/working")
      }
    } catch (error) {
      showToast({
        icon: "alert",
        title:
          "We could not verify whether the models were pushed, please try again soon.",
      })
    } finally {
      setIsRevalidating(false)
    }
  }

  return {
    isRevalidating,
    revalidate,
  }
}

function useOnboardingAssets(stepName: keyof typeof onboardingWithImages) {
  const { starterId } = useRepository()
  const onboardingDropOffFlag = useOnboardingDropOffFlag()
  const { framework } = useRepository()
  const isSupportedFramework = getIsSupportedFramework(framework)
  const shouldShowOnboardingWithVideos =
    !starterId && onboardingDropOffFlag === "videos" && isSupportedFramework

  if (shouldShowOnboardingWithVideos && stepName in onboardingWithVideos) {
    return {
      video:
        onboardingWithVideos[stepName as keyof typeof onboardingWithVideos],
    }
  }

  return { image: onboardingWithImages[stepName] }
}

interface CommonStepProps {
  shouldAnimate: boolean
}

interface ImageStepProps extends CommonStepProps {
  image: string
  video?: never
  children: ReactNode
}

interface VideoStepProps extends CommonStepProps {
  video: string
  image?: never
  children: ReactNode
}

type StepProps = ImageStepProps | VideoStepProps

export function Step(props: StepProps) {
  const { children, image, video, shouldAnimate = false } = props

  const { key } = useLocation()
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shouldAnimate) return
    cardRef.current?.animate([{ transform: "scale(1.1)", offset: 0.7 }], {
      duration: 500,
      easing: "ease",
    })
  }, [key, shouldAnimate])

  return (
    <Card
      ref={cardRef}
      padding={0}
      sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
      variant="outlined"
    >
      {image && <CardImage src={image} height={280} />}
      {video && <CardVideo src={video} height={280} autoPlay />}

      <CardContent padding={32} gap={0}>
        {children}
      </CardContent>
    </Card>
  )
}

function WelcomeStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { goToNextStep } = useOnboarding()
  const onboardingAssets = useOnboardingAssets("welcome")

  usePrefetchDefaultLocalesQuery()
  usePrefetchRepositoryLocalesQuery()

  const onContinue = () => {
    goToNextStep()
    trackPbOnboardingStep("welcome")
  }

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Welcome to your Page Builder
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        This is where you'll create and edit pages. But first you'll have to
        define the models for your pages with Slice Machine.
      </Text>

      <Button endIcon="arrowForward" size="large" onClick={onContinue}>
        Get started
      </Button>
    </Step>
  )
}

function LaunchStarterStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { revalidate, isRevalidating } = useRevalidateCustomTypes()

  const { goToPreviousStep } = useOnboarding()
  const { completeStep } = useSharedOnboarding()

  const hasTriggeredStepCompleteRef = useRef(false)

  const hasCustomTypes = useHasCustomTypes()
  const { starterId } = useRepository()
  const repositoryName = getRepositoryName()
  const onboardingAssets = useOnboardingAssets("launchStarter")

  const starterRepo = (starterId && starterRepositories[starterId]) ?? ""

  function onContinue() {
    void revalidate()
    trackPbOnboardingStep("launchStarter")
  }

  useEffect(() => {
    if (hasCustomTypes && !hasTriggeredStepCompleteRef.current) {
      hasTriggeredStepCompleteRef.current = true
      void completeStep("createProject", "setupSliceMachine")
    }
  }, [hasCustomTypes, completeStep])

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Launch your starter
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Your starter has everything you need to get started, including Prismic's
        development tool, Slice Machine. Run this command to download and
        install your starter:
      </Text>

      <CodeBlock
        code={`npx @slicemachine/init@latest --repository ${repositoryName} --starter ${starterRepo}`}
        language="bash"
        title="Terminal"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button
          color="grey"
          size="large"
          onClick={goToPreviousStep}
          disabled={isRevalidating}
        >
          Back
        </Button>

        <Button
          endIcon="arrowForward"
          size="large"
          onClick={onContinue}
          loading={isRevalidating}
        >
          I'm done
        </Button>
      </Box>
    </Step>
  )
}

function PushModelsStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { revalidate, isRevalidating } = useRevalidateCustomTypes()
  const { goToPreviousStep } = useOnboarding()
  const onboardingAssets = useOnboardingAssets("pushModels")

  const onContinue = () => {
    void revalidate()
    trackPbOnboardingStep("pushModels")
  }

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Push your models from Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Create one or more custom types in{" "}
        <a
          href="http://localhost:9999"
          target="_blank"
          style={{ textDecoration: "none" }}
          rel="noreferrer"
        >
          Slice Machine
        </a>{" "}
        and push them to Prismic. Then come back here and continue.
      </Text>

      <Button
        invisible
        asChild
        startIcon="arrowForward"
        sx={{
          alignSelf: "start",
        }}
      >
        <a
          href="https://prismic.io/docs/content-modeling"
          target="_blank"
          rel="noreferrer"
        >
          Learn more about creating content models.
        </a>
      </Button>

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button
          color="grey"
          size="large"
          onClick={goToPreviousStep}
          disabled={isRevalidating}
        >
          Back
        </Button>

        <Button
          endIcon="arrowForward"
          size="large"
          onClick={onContinue}
          loading={isRevalidating}
        >
          I've pushed my models
        </Button>
      </Box>
    </Step>
  )
}

function RunSliceMachineStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { goToPreviousStep, goToNextStep } = useOnboarding()
  const { completeStep } = useSharedOnboarding()
  const onboardingAssets = useOnboardingAssets("runSliceMachine")

  const onContinue = () => {
    goToNextStep()
    void completeStep("setupSliceMachine")
    trackPbOnboardingStep("runSliceMachine")
  }

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Run Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Enter this command to run Slice Machine locally. Once it's running, open
        it at{" "}
        <a
          href="http://localhost:9999"
          target="_blank"
          style={{ textDecoration: "none" }}
          rel="noreferrer"
        >
          localhost:9999
        </a>
        .
      </Text>

      <CodeBlock code="npm run slicemachine" language="bash" title="Terminal" />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Button endIcon="arrowForward" size="large" onClick={onContinue}>
          Continue
        </Button>
      </Box>
    </Step>
  )
}

function SetupSliceMachineStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { goToPreviousStep, goToNextStep } = useOnboarding()
  const { completeStep } = useSharedOnboarding()
  const repositoryName = getRepositoryName()
  const onboardingAssets = useOnboardingAssets("launchStarter")

  // The stored project is persisted from the previous step "Create project"
  // If the user has followed the guide with our code snippets, their local project
  // will have the stored name, if they haven't we use the fallback
  const storedProject = localStorage.read(onboardingSelectedProject)
  const projectName = storedProject || "<your project name>"

  const onContinue = () => {
    goToNextStep()
    void completeStep("setupSliceMachine")
    trackPbOnboardingStep("setupSliceMachine")
  }

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Set up Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Slice Machine is the Prismic tool for creating models. Install it in
        your Next.js project by running these commands:
      </Text>

      <CodeBlock
        code={`cd ${projectName}
npx @slicemachine/init@latest --repository ${repositoryName}`}
        language="bash"
        title="Terminal"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Button endIcon="arrowForward" size="large" onClick={onContinue}>
          Continue
        </Button>
      </Box>
    </Step>
  )
}

function CreateProjectStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const { goToNextStep, goToPreviousStep } = useOnboarding()
  const { completeStep } = useSharedOnboarding()
  const onboardingAssets = useOnboardingAssets("createProject")

  const { framework } = useRepository()
  const repositoryName = getRepositoryName()

  // if the user has selected a unsupported framework we allow them to go straight to the custom types builder
  if (!getIsSupportedFramework(framework)) {
    return <UnsupportedFrameworkStep shouldAnimate={false} />
  }

  const label = frameworkLabels[framework]
  const createProjectCodeSnippet = frameworkCreateProjectCodeSnippet[framework]

  function goToNextStepAndSelectProject(project: string) {
    goToNextStep()
    localStorage.write(onboardingSelectedProject, project)
    void completeStep("createProject")
    trackPbOnboardingStep("createProject")
  }

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Create a {label} project
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Before we get started, you need a {label} project to work with. Open
        your terminal and enter this command:
      </Text>

      <CodeBlock
        code={`${createProjectCodeSnippet} ${repositoryName}`}
        language="bash"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Box gap={16} alignItems="center">
          <Button
            invisible
            color="grey"
            onClick={() => {
              goToNextStepAndSelectProject("")
            }}
          >
            I already have a project
          </Button>
          <Button
            endIcon="arrowForward"
            size="large"
            onClick={() => {
              goToNextStepAndSelectProject(repositoryName)
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Step>
  )
}

export function UnsupportedFrameworkStep(props: CommonStepProps) {
  const { shouldAnimate } = props
  const baseRepoUrl = getBaseRepositoryUrl()
  const customTypeBuilderUrl = `${baseRepoUrl}/masks`
  const onboardingAssets = useOnboardingAssets("createProject")

  return (
    <Step shouldAnimate={shouldAnimate} {...onboardingAssets}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Build your first custom type
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        Custom types allow you to define and configure fields for your content.
        Define custom types for pages, posts, events, authors, products, places,
        ...
      </Text>

      <Button size="large" asChild>
        <Link to={customTypeBuilderUrl}>Create custom type</Link>
      </Button>
    </Step>
  )
}

export function OnboardingStepsSkeleton() {
  return (
    <Box padding={{ top: 80 }} width="100%" justifyContent="center">
      <Skeleton width={498} height="80%" />
    </Box>
  )
}

// this reference points at the starter repositories users can choose between
// when setting up their initial Prismic repository
const starterRepositories: Record<string, string> = {
  next_minimal: "nextjs-starter-prismic-minimal",
  next_multi_page: "nextjs-starter-prismic-multi-page",
  nuxt_minimal: "nuxt-starter-prismic-minimal",
  nuxt_multi_page: "nuxt-starter-prismic-multi-page",
  sveltekit_minimal: "sveltekit-starter-prismic-minimal",
  sveltekit_multi_page: "sveltekit-starter-prismic-multi-page",
}
